import { Stack, Text, useRadioGroup } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { DiscountClass } from 'types/cart/PromoCode';
import { PurchaseType } from 'types/product/PurchaseType';
import type { Subscription } from 'types/product/Subscription';
import type { Variant } from 'types/product/Variant';

import type { BaseConfiguratorProps } from '.';
import { ProductConfiguratorPlanItem } from './ui/ProductConfiguratorPlanItem';
import { Wrapper } from './Wrapper';

interface SubscriptionMultiVariantProps extends BaseConfiguratorProps {
  subscriptions: Subscription[];
  mostPopularVariantId?: string;
  bestValueVariantId?: string;
  selectedVariantsIds?: string[];
  availableForSale: boolean;
  otpVariant: Variant;
}

export function SubscriptionMultiVariant({
  availableForSale,
  subscriptions,
  mostPopularVariantId,
  bestValueVariantId,
  selectedVariantsIds,
  showSavingsAsAmount,
  isDiscountHidden,
  isSupplementPicker,
  purchaseType,
  promoCodes,
  ...props
}: SubscriptionMultiVariantProps) {
  const { otpVariant } = props;

  const selectedSubscriptions = selectedVariantsIds?.length
    ? subscriptions.filter((subscription) =>
        selectedVariantsIds.includes(subscription.id),
      )
    : subscriptions;

  const availableSubscriptions = selectedSubscriptions.filter(
    (item) => item.availableForSale,
  );

  const hasAvailableSubscriptions = !!availableSubscriptions.length;
  const isAvailableForSale = availableForSale && hasAvailableSubscriptions;

  const defaultVariantId = props.selectedVariant?.id || mostPopularVariantId;

  const selectedIndex = Math.max(
    0,
    availableSubscriptions.findIndex((item) => item.id === defaultVariantId),
  );

  const defaultSubscription = hasAvailableSubscriptions
    ? availableSubscriptions[selectedIndex]
    : null;

  const [selectedVariant, setSelectedVariant] = useState<Subscription | null>(
    defaultSubscription,
  );

  useEffect(() => {
    setSelectedVariant(defaultSubscription);
  }, [defaultSubscription]);

  const currentSelected =
    selectedSubscriptions.find(
      (item) => item.sellingPlan.id === selectedVariant?.sellingPlan?.id,
    ) ?? defaultSubscription;

  const handleSelect = (val: string) => {
    const subsciption = selectedSubscriptions?.find(
      (item) => item.sellingPlan.id === val,
    );

    if (!subsciption) return;
    setSelectedVariant(subsciption);
    props.setSelectedVariant?.(subsciption);
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'subscription',
    value: currentSelected?.sellingPlan?.id,
    onChange: handleSelect,
  });

  const group = getRootProps();

  const hasOneOption = selectedSubscriptions?.length === 1;

  return (
    <Wrapper
      className="product_subscriptionMultiVariant"
      variant={currentSelected || otpVariant}
      availableForSale={isAvailableForSale}
      showOTPLink={!purchaseType}
      purchaseType={PurchaseType.SUB}
      isDiscountHidden={isDiscountHidden}
      isSupplementPicker={isSupplementPicker}
      promoCodes={promoCodes}
      {...props}
    >
      {isSupplementPicker ? (
        <Text fontWeight="medium" size="title2" as="h2" display="block">
          Subscription.{' '}
          <Text as="span" size="title2" color="blackAlpha.600">
            Choose quantity.
          </Text>
        </Text>
      ) : (
        <Text fontWeight="medium" size="title2" as="h2" display="block">
          Subscribe & Save:
        </Text>
      )}
      <Stack spacing="3xs" {...group} aria-label="Subscription options">
        {selectedSubscriptions?.map((sub) => {
          const radio = getRadioProps({
            value: sub.sellingPlan.id,
          });

          return (
            <ProductConfiguratorPlanItem
              key={sub.id}
              isMostPopular={sub.id === mostPopularVariantId}
              isBestValue={sub.id === bestValueVariantId}
              isDisabled={!sub.availableForSale}
              isClickable={!hasOneOption}
              showSavingsAsAmount={showSavingsAsAmount}
              isDiscountHidden={isDiscountHidden}
              purchaseType={purchaseType}
              discountCodes={
                promoCodes
                  ?.filter(
                    ({ discountClass }) =>
                      discountClass === DiscountClass.PRODUCT,
                  )
                  .map(({ code }) => code) || []
              }
              {...sub}
              {...radio}
            />
          );
        })}
      </Stack>
    </Wrapper>
  );
}
